import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Radio, Calendar, Clock, ArrowRight, Video, Star, Flame, Archive } from 'lucide-react';
import { sessions } from '../data/sessions';

// Re-export for detail page
export { sessions };

// Filter only upcoming sessions
const upcomingSessions = sessions.filter(s => s.status === 'upcoming');
const allTags = [...new Set(upcomingSessions.flatMap(s => s.tags))];

const LiveSessionsPage = () => {
  const [selectedTags, setSelectedTags] = useState([]);

  const toggleTag = (tag) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const filteredSessions = selectedTags.length === 0 ? upcomingSessions : upcomingSessions.filter(s => s.tags.some(tag => selectedTags.includes(tag)));

  const getTypeColor = (type) => {
    switch(type) {
      case 'Live Hack': return 'bg-[var(--error)]/20 text-[var(--error)]';
      case 'Q&A': return 'bg-[var(--accent-cyan)]/20 text-[var(--accent-cyan)]';
      case 'Tool Demo': return 'bg-[var(--accent-purple)]/20 text-[var(--accent-purple)]';
      case 'CTF': return 'bg-[#FFD700]/20 text-[#FFD700]';
      case 'Talk': return 'bg-[var(--accent-purple)]/20 text-[var(--accent-purple)]';
      default: return 'bg-[var(--primary)]/20 text-[var(--primary)]';
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="live-sessions-page">
      {/* Hero */}
      <section className="py-12 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="hero-glow top-0 left-1/4" />
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <Link to="/services" className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--primary)] mb-6 font-mono text-sm">
              <ArrowRight className="w-4 h-4 rotate-180" /> Back to Services
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 border border-[var(--error)] bg-[var(--error)]/10 flex items-center justify-center">
                <Radio className="w-8 h-8 text-[var(--error)]" />
              </div>
              <div>
                <p className="font-mono text-sm uppercase tracking-wider text-[var(--error)]">Interactive Events</p>
                <h1 className="text-3xl md:text-5xl font-bold text-[var(--text-main)]">Live Sessions</h1>
              </div>
            </div>
            <p className="text-[var(--text-muted)] text-lg md:text-xl leading-relaxed">
              Join our live hacking sessions, Q&As, tool demos, and CTF nights. Interact with experts in real-time.
            </p>
            {/* <Link to="/services/live-sessions/archive" className="inline-flex items-center gap-2 mt-6 text-[var(--text-muted)] hover:text-[var(--error)] font-mono text-sm border border-[var(--border)] px-4 py-2 hover:border-[var(--error)] transition-colors" data-testid="view-archive-link">
              <Archive className="w-4 h-4" /> View Past Sessions
            </Link> */}
          </div>
        </div>
      </section>

      {upcomingSessions.length === 0 ? (
        <section className="py-16 md:py-24 relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-30" />
          <div className="hero-glow top-0 right-1/4" />
          <div className="container-main relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="bg-[var(--surface)] border border-[var(--border)] p-8 md:p-12">
                <div className="space-y-6">
                  <p className="text-[var(--error)] text-sm font-mono uppercase tracking-wider">Upcoming Sessions</p>
                  <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-main)]">Live Sessions Coming Soon</h2>
                  <p className="text-[var(--text-muted)] text-lg leading-relaxed">We're scheduling exciting live hacking sessions, Q&As, tool demos, and CTF nights. Interact with experts in real-time.</p>
                  <p className="text-[var(--text-muted)] text-base">Check back soon for announcements on upcoming live events.</p>
                </div>
              </div>
              <div className="bg-[var(--surface-highlight)] border border-[var(--border)] border-dashed p-8 md:p-12 flex flex-col items-center justify-center text-center space-y-4">
                <div className="text-5xl">📡</div>
                <p className="text-[var(--text-muted)] text-lg">Live sessions arriving soon</p>
                <p className="text-sm text-[var(--text-muted)]">Follow us for updates on session schedules and speaker announcements.</p>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <>
          {/* Filter */}
          <section className="py-8 border-y border-[var(--border)] ">
            <div className="container-main">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <span className="font-mono text-sm text-[var(--error)] uppercase tracking-wider">Filter by Tag:</span>
                <div className="flex flex-wrap gap-2">
                  {allTags.map((tag) => (
                    <button key={tag} onClick={() => toggleTag(tag)} className={`px-4 py-2 font-mono text-sm border transition-colors duration-200 ${selectedTags.includes(tag) ? 'border-[var(--error)] bg-[var(--error)]/20 text-[var(--error)]' : 'border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--error)]/50'}`} data-testid={`tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}>{tag}</button>
                  ))}
                  {selectedTags.length > 0 && (
                    <button onClick={() => setSelectedTags([])} className="px-4 py-2 font-mono text-sm border border-[var(--error)]/50 text-[var(--error)] hover:bg-[var(--error)]/10">Clear All</button>
                  )}
                </div>
              </div>
              <p className="mt-4 text-sm text-[var(--text-muted)]">Showing {filteredSessions.length} of {upcomingSessions.length} upcoming sessions</p>
            </div>
          </section>

          {/* Sessions List */}
          <section className="py-16">
            <div className="container-main">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredSessions.map((session) => (
                  <div key={session.id} className="bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--error)] transition-colors duration-300 overflow-hidden group relative" data-testid={`session-card-${session.id}`}>
                    {/* Featured & Popular Badges */}
                    <div className="absolute top-4 right-4 flex gap-2 z-10">
                      {session.featured && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] text-xs font-mono uppercase" data-testid={`featured-badge-${session.id}`}>
                          <Star className="w-3 h-3 fill-[#FFD700]" /> Featured
                        </span>
                      )}
                      {session.popular && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-[#FF6B00]/20 border border-[#FF6B00] text-[#FF6B00] text-xs font-mono uppercase" data-testid={`popular-badge-${session.id}`}>
                          <Flame className="w-3 h-3" /> Popular
                        </span>
                      )}
                    </div>
                    <div className="p-6">
                      <span className={`inline-block px-3 py-1 text-xs font-mono uppercase mb-3 ${getTypeColor(session.type)}`}>{session.type}</span>
                      <h3 className="text-lg font-bold text-[var(--text-main)] group-hover:text-[var(--error)] transition-colors mb-3 mt-4">{session.title}</h3>
                      <p className="text-[var(--text-muted)] text-sm mb-4 line-clamp-2">{session.description}</p>
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[var(--error)]" /><span className="text-sm text-[var(--text-main)] font-mono">{session.date}</span></div>
                        <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[var(--error)]" /><span className="text-sm text-[var(--text-main)] font-mono">{session.time} ({session.duration})</span></div>
                        <div className="flex items-center gap-2"><Video className="w-4 h-4 text-[var(--error)]" /><span className="text-sm text-[var(--text-main)] font-mono">{session.host}</span></div>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {session.tags.map((tag, idx) => (<span key={idx} className="px-2 py-1 bg-[var(--surface-highlight)] border border-[var(--border)] text-xs text-[var(--text-muted)] font-mono">{tag}</span>))}
                      </div>
                      {session.speakers && session.speakers.length > 0 && (
                        <div className="flex flex-wrap gap-3 mb-4">
                          {session.speakers.map((speaker, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              {speaker.photo ? (
                                <img src={speaker.photo} alt={speaker.name} className="w-8 h-8 rounded-full object-cover border border-[var(--border)]" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-[var(--surface-highlight)] border border-[var(--border)] flex items-center justify-center text-xs font-mono text-[var(--text-muted)]">
                                  {speaker.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                </div>
                              )}
                              <div>
                                <p className="text-xs font-mono text-[var(--text-main)] leading-none">{speaker.name}</p>
                                {(speaker.designation || speaker.company) && (
                                  <p className="text-xs font-mono text-[var(--text-muted)] mt-0.5">
                                    {[speaker.designation, speaker.company].filter(Boolean).join(' @ ')}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <Link to={`/services/live-sessions/${session.id}`} className="w-full bg-[var(--primary)] text-black font-mono font-bold px-4 py-3 text-sm uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 text-center block" data-testid={`know-more-btn-${session.id}`}>Know More</Link>
                    </div>
                  </div>
                ))}
              </div>
              {filteredSessions.length === 0 && (
                <div className="text-center py-16">
                  <p className="text-[var(--text-muted)] text-lg">No sessions match the selected filters.</p>
                  <button onClick={() => setSelectedTags([])} className="mt-4 text-[var(--error)] font-mono hover:underline">Clear filters</button>
                </div>
              )}
            </div>
          </section>
        </>
      )}
      <div className="container-main flex justify-center mb-12">
        <Link to="/services/live-sessions/archive" className="inline-flex items-center gap-2 mt-6 text-[var(--text-muted)] hover:text-[var(--accent-purple)] font-mono text-sm border border-[var(--border)] px-4 py-2 hover:border-[var(--accent-purple)] transition-colors" data-testid="view-archive-link">
          <Archive className="w-4 h-4" /> View Past Programs
        </Link>
      </div>
    </div>
  );
};

export default LiveSessionsPage;
